import { isAxiosError } from "axios";
import { toast } from "react-hot-toast";
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

const CustomToastUI = ({ t, message, type }) => {
  const icons = {
    success: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    error: <AlertCircle className="w-4 h-4 text-rose-500" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    info: <Info className="w-4 h-4 text-blue-500" />,
  };

  const colors = {
    success: 'border-l-4 border-l-emerald-500 border-slate-200 bg-white text-slate-800',
    error: 'border-l-4 border-l-rose-500 border-slate-200 bg-white text-slate-800',
    warning: 'border-l-4 border-l-amber-500 border-slate-200 bg-white text-slate-800',
    info: 'border-l-4 border-l-blue-500 border-slate-200 bg-white text-slate-800',
  };

  return (
    <div
      className={`${t.visible ? 'animate-in slide-in-from-right-10' : 'animate-out fade-out slide-out-to-right-10'} duration-300 pointer-events-auto`}
    >
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-[0_10px_30px_rgba(0,0,0,0.08)] backdrop-blur-md ${colors[type] || colors.info}`}>
        {icons[type] || icons.info}
        <span className="text-[13px] font-semibold text-slate-700 tracking-tight whitespace-pre-wrap">{message}</span>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="ml-2 p-1 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
        >
          <X className="w-3.5 h-3.5 opacity-50" />
        </button>
      </div>
    </div>
  );
};

export const showSuccessMessage = (message) => {
  return toast.custom((t) => <CustomToastUI t={t} message={message} type="success" />, { position: "bottom-right", duration: 2500 });
};

export const showErrorMessage = (message) => {
  return toast.custom((t) => <CustomToastUI t={t} message={message} type="error" />, { position: "bottom-right", duration: 3500 });
};

export const showWarningMessage = (message) => {
  return toast.custom((t) => <CustomToastUI t={t} message={message} type="warning" />, { position: "bottom-right", duration: 3000 });
};

export const showInfoMessage = (message) => {
  return toast.custom((t) => <CustomToastUI t={t} message={message} type="info" />, { position: "bottom-right", duration: 2500 });
};

export const showApiError = (error) => {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    let errorMessage = data?.message || "Something went wrong";

    if (data?.errors?.fieldErrors) {
      const fieldErrors = Object.entries(data.errors.fieldErrors)
        .map(([field, msgs]) => {
          const cleanField = field.replace('body.', '').replace('query.', '').replace('params.', '');
          return `${cleanField}: ${msgs.join(', ')}`;
        });
      
      if (fieldErrors.length > 0) {
        errorMessage = `Validation failed:\n${fieldErrors.join('\n')}`;
      }
    } else if (data?.error) {
      errorMessage = typeof data.error === 'object' ? (data.error.message || JSON.stringify(data.error)) : data.error;
    }

    return toast.custom((t) => <CustomToastUI t={t} message={errorMessage} type="error" />, {
      position: "bottom-right",
      duration: 3500,
    });
  } else {
    return toast.custom((t) => <CustomToastUI t={t} message={error.message || "Something went wrong"} type="error" />, {
      position: "bottom-right",
      duration: 2500,
    });
  }
};
